import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { isDisplayCurrency } from "../../components/economy/earnDemo/earnDemoCopy";
import axiosInstance from "../../utils/axiosInstance";
import { FLOOR_TERMINAL } from "./floorStates";

/**
 * economy - the authenticated-self economy read (W4-F1, technical brief 4.7).
 *
 * It backs `GET api/v1/me/economy?challenge={id}` (W4-B1, extended by B2 and
 * B3), which answers for the CALLER ONLY: the server's enablement flag and
 * incentive version, the caller's own personal XP and streak, the caller's
 * squad's standing and awards, the caller's own output's floor state, and the
 * cohort's Vault when one is funded. It ships disabled and the read says so.
 *
 * WHY IT IS A THIRD SLICE AND NOT A FIELD ON THE PROJECTION OR ON COMPLETION.
 * The experience projection is SQUAD-SHARED and every authorized caller of a
 * squad must receive an identical payload; `completion` is the Wave 3 read and
 * is scoped to a different question. Every per-caller economy fact lives here,
 * so the rule that made the projection safe to share is not quietly weakened by
 * one convenient field, and a Wave 3 surface cannot start depending on a Wave 4
 * one by accident.
 *
 * IDENTITY IS `(userId, challengeId)` AND THE SCOPE KEY IS HOW IT IS ENFORCED,
 * exactly as `completion`'s is. `pending` clears the held payload the moment
 * the scope changes, so a learner switching account or challenge on one tab
 * never sees the prior scope's standing or the prior learner's streak. The
 * take-latest `requestId` covers the case the scope key cannot: two overlapping
 * reads for the SAME scope, where both carry the same arg and only order
 * separates them.
 *
 * `enabled` IS FALSE UNTIL A MATCHING SUCCESS, AND THAT IS THE FLAG CONTRACT.
 * Every Wave 4 surface is mounted on it: with it false the dashboard, the
 * header and the endings render today's tree byte for byte. So the initial
 * value, the value under a scope change, and the value after a refusal are all
 * false. There is no state of this slice in which a reward beat renders because
 * the read has not answered yet.
 *
 * THE SHARED SOCKET IS NOT AN AUTHORITY AND THIS FILE IS WHERE THAT IS
 * ENFORCED (technical brief 4.4). The squad economy message is broadcast to
 * every member and therefore carries `user_id: null` and `user_points: null` BY
 * CONTRACT, for every event including a personal one. A reducer that fed those
 * into a personal field would zero the learner's own XP for every teammate's
 * action. So no socket message in this slice ever writes `personal`, `squad`,
 * or `vault`: a message is a NOTIFICATION THAT THE READ IS STALE, and the
 * authenticated read is the only thing that ever sets a number. The one fact a
 * message does write is the floor transition, which is not served on any other
 * channel in time for the submission boundary to use it, and it is written
 * through `mergeOutput` so a read cannot undo a fresher one.
 */

let economyRequestSeq = 0;

/**
 * HOW LONG THE ECONOMY READ MAY HANG (W4-F2.3 fix batch item 5, Codex M2).
 *
 * `shouldFetchEconomy` refuses every dispatch while `status === "loading"`,
 * which is the take-latest discipline this slice needs. axios applies no
 * timeout by default and `axiosInstance` sets none, so a single request the
 * server accepts and never answers -- a stalled proxy, a backend that holds
 * the socket -- left `status` at `loading` for the life of the page, and
 * NOTHING could ask again: not the pending-result poll, not the socket-absent
 * beat, not the XP re-read. F33's whole claim is that a result never depends
 * on a transport being healthy, and without this that claim rested on the HTTP
 * layer never hanging.
 *
 * FIFTEEN SECONDS because the poll's own first delay is two and its cap is a
 * hundred and twenty: a read still out after fifteen has already missed the
 * beat it was for, and failing it costs one poll tick rather than the page's
 * whole remaining session.
 *
 * IT IS PER REQUEST AND THE INSTANCE'S DEFAULT IS UNCHANGED. Every other
 * caller in the app behaves exactly as it does today.
 */
export const ECONOMY_READ_TIMEOUT_MS = 15000;

/** How many socket event ids are remembered for the duplicate check. */
export const SEEN_EVENT_CAP = 64;

/**
 * THE TWO FLOOR LISTS, DECLARED IN `floorStates.js` AND RE-EXPORTED HERE.
 *
 * They were declared in this file and every importer still reads them from it.
 * They moved because the pure liveness policy has to read them and importing
 * this module for a constant drags axios's ESM build into any suite that
 * touches that policy (W4-F2.3, F33).
 */
export { FLOOR_IN, FLOOR_TERMINAL } from "./floorStates";

/** The socket message types this slice consumes. */
export const ECONOMY_MESSAGE_TYPES = Object.freeze([
  "economy",
  "floor",
  "send_back",
  "vault",
  "award",
]);

/**
 * THE ONE MESSAGE WHOSE `squad_id` IS NOT THE RECIPIENT'S SQUAD (W4-F2).
 *
 * An award is announced on EVERY included squad's channel, and the payload's
 * `squad_id` is the HONORED squad (`AwardService.php`: one payload, fanned out
 * to every squad id in the cohort). So the squad check that protects `floor`
 * and `send_back`, whose `squad_id` really is the channel's, would refuse every
 * award won by anybody else, which is most of them, and the learner would only
 * ever be told about their own squad's wins.
 *
 * THE COHORT CHECK STILL APPLIES and is the right scope for an award: the
 * payload carries `cohort_id` and the decision is a cohort fact.
 *
 * AND THE MESSAGE IS STILL ONLY A STALENESS NOTICE. It carries no
 * `award_notice_id` and no `mine`, which are the two fields the pop-up is keyed
 * on (technical brief 5.4 and 4.7), so it could not be a value here even if the
 * contract allowed one: the read's `notices[]` is where both live, and the
 * refresh this message triggers is what fetches them.
 */
const SQUAD_SCOPE_EXEMPT = Object.freeze(["award"]);

/** The scope a held payload belongs to. Empty ids never produce a key. */
export const economyScopeKey = ({ userId, challengeId } = {}) =>
  userId && challengeId ? `${userId}:${challengeId}` : null;

/**
 * @param {object} arg
 * @param {number|string} arg.userId the authenticated learner. It is NOT sent
 *   to the server: the route reads `auth('api')->user()` and the FormRequest
 *   rejects a `user_id` parameter. It is the client half of the scope key, so
 *   the reducers can tell a response for the previous account from one for
 *   this.
 * @param {number|string} arg.challengeId
 * @param {AbortSignal} [arg.signal]
 */
export const fetchEconomy = createAsyncThunk(
  "economy/fetch",
  async ({ challengeId, signal, table = false } = {}, thunkAPI) => {
    try {
      const response = await axiosInstance(
        "/me/economy",
        "get",
        undefined,
        // `params` and `signal` are promoted out of customHeaders to top-level
        // axios config by axiosInstance; passing the challenge as a header
        // would reach the server as a header and the FormRequest would reject
        // the request for a missing `challenge`.
        //
        // W4-F2: `table` ASKS FOR `squad.all` AND NOTHING ELSE (technical brief
        // 4.7, which scopes `?table=1` to the Cup's "see all squads"). It is
        // off by default because the full standings table is more of the
        // cohort than any other surface needs, and the Cup is the one page that
        // asks: it needs the served `place` on every row so ties render as
        // ties, and the cohort's size, which is the only thing `bandLabel` can
        // honestly derive a band from.
        {
          params: table
            ? { challenge: challengeId, table: 1 }
            : { challenge: challengeId },
          signal: signal ?? thunkAPI.signal,
          /* BOUNDED (fix batch item 5). `axiosInstance` promotes this to
             axios config; see `ECONOMY_READ_TIMEOUT_MS`. */
          timeout: ECONOMY_READ_TIMEOUT_MS,
        },
      );
      return response.data.data;
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError")
        throw err;
      return thunkAPI.rejectWithValue({
        status: err?.response?.status ?? null,
        message: "Failed to load economy.",
      });
    }
  },
  { idGenerator: () => `eco-${++economyRequestSeq}` },
);

/** The withheld output, which is also what a legacy cohort serves. */
const EMPTY_OUTPUT = Object.freeze({
  attempt: null,
  review_state: null,
  send_back_comment: null,
  floor_status: null,
  floor_hint: null,
});

const initialState = {
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  /**
   * HOW MANY READS HAVE STARTED, ever, for this slice. The same instrument, for
   * the same reason, that `completion` and `experienceProjection` carry: a
   * caller that re-reads at a boundary needs to express "newer than the
   * boundary", and `succeeded` alone cannot, because the held answer is already
   * `succeeded` at the instant the boundary fires.
   */
  generation: 0,
  error: null,
  errorStatus: null,
  requestId: null,
  scopeKey: null,
  /** The server's flag. FALSE until a matching success says otherwise. */
  enabled: false,
  incentiveVersion: null,
  /**
   * THE CALLER'S OWN NUMBERS, AND THE ONLY WRITER IS THE READ. Never a socket.
   */
  personal: null,
  squad: null,
  output: EMPTY_OUTPUT,
  vault: null,
  /**
   * W4-F2.3, F36: WHETHER THE RUN HAS A PRIZE AT ALL, AS A SEPARATE FACT.
   *
   * `vault` is the FROZEN Vault and `VaultReadDTO::read` serves null until
   * `frozen_at` is set, which happens at the squad's FIRST LEVEL ENTRY. The
   * kickoff cinematic runs before that, so the one line that names the prize
   * was dropped at exactly the moment it mattered: Amir played the whole
   * kickoff on 2026-09-15 and was never told the Vault exists.
   *
   * SO THE KICKOFF READS THIS AND NEVER `vault`. The two answer different
   * questions: "is there a prize" is true from the moment the run is funded,
   * "what is in the Vault right now" is only true once it is frozen.
   *
   * THE CLIENT READS ITS PRESENCE AND NOTHING INSIDE IT. No amount reaches any
   * learner surface from here, by NO BUILD trigger 5 of the technical brief.
   */
  vaultPrize: null,
  weights: [],
  finaleWeight: null,
  runClosed: false,
  kickoffSeen: false,
  levels: [],
  notices: [],
  rules: null,
  /**
   * THE COHORT THE SOCKET IS ALLOWED TO SPEAK FOR.
   *
   * The read does not serve a cohort id, and the economy message does, so the
   * scope has to be told to this slice by the one component that knows it. It
   * is held HERE rather than read across slices from inside a reducer, so the
   * rejection rule is a pure function of this slice's own state and is table
   * testable without a store.
   */
  cohortId: null,
  /**
   * THE SQUAD THE SOCKET IS ALLOWED TO SPEAK FOR.
   *
   * `floor` and `send_back` both carry `squad_id` and neither carries a cohort,
   * and a capsule id is shared by every cohort and every squad of one program.
   * So the squad is the only scope those two messages can be refused by, and a
   * learner whose channel ever carried a second squad's traffic would otherwise
   * take that squad's floor result as their own.
   */
  squadId: null,
  /** The last `SEEN_EVENT_CAP` accepted socket event ids, newest last. */
  seenEventIds: [],
  /**
   * BUMPED ON EVERY ACCEPTED SOCKET MESSAGE AND ON A RECONNECT. The dashboard's
   * one read owner watches it. A counter rather than a boolean: two messages
   * arriving between two renders must still produce one refresh each, and a
   * boolean that was already true would swallow the second.
   */
  refreshToken: 0,
  /**
   * THE NEWEST ACCEPTED FLOOR TRANSITION, or null.
   *
   * `{ eventId, capsuleId, attempt, status, hint, readerFailureReason }`.
   * This is what the submission boundary matches its parked ship event
   * against, and what tells the dashboard to mark the experience projection
   * stale so an open tab refreshes its Submitted mark.
   */
  floor: null,
};

/**
 * WHICH CAPSULE THE READ'S `output` BLOCK BELONGS TO, FROM THE READ (B2.1 item
 * 3, W4-F2.1).
 *
 * `EconomyReadDTO` now names it: `output.capsule_id` is the capsule the server
 * resolved as the squad's current level and served the output for. F1 had to
 * RE-DERIVE that from `levels[]` ("the last capsule the squad has entered, else
 * the first") because the read did not say, and a re-derivation of a server
 * rule is a second copy of it: the day the two disagreed about which capsule
 * the output belonged to would be the day a floor result for one level released
 * the ending for another. The server says it now, so the client reads it.
 *
 * IT IS ABSENT RATHER THAN NULL ON A WITHHELD READ, and that is the contract:
 * the `enabled: false` payload and the no-current-capsule payload both build
 * the output block without the key. Absent reads as null here, and null means
 * "the client cannot say which capsule this is about", which every caller
 * treats as do not act.
 *
 * IT LIVES HERE AND NOT IN THE DASHBOARD BECAUSE THE SLICE IS THE ONE THAT HAS
 * TO REFUSE A FOREIGN FLOOR, and the Dashboard imports this same function.
 *
 * @param {object|null|undefined} output the read's `output` block
 * @returns {number|null} the capsule id, or null where the read did not say
 */
export const outputCapsuleIdOf = (output) => {
  const id = output?.capsule_id;
  if (id === null || id === undefined) return null;
  const value = Number(id);
  return Number.isFinite(value) ? value : null;
};

/**
 * IS THIS COHORT ON A VERSIONED ECONOMY, FOR THE SURFACES THAT ONLY NEED TO
 * KNOW WHETHER TO KEEP QUIET (W4-F2.2 fix batch item 11, Fable m2).
 *
 * The two fields the read serves and nothing inferred, which is the same test
 * `Dashboard.jsx` makes for the submission boundary. It lives here because four
 * task and challenge surfaces and the bookmark modal each print a hardcoded XP
 * figure that is simply false once a run has its own ledger, and five copies of
 * `enabled && incentiveVersion !== null` spread across components that
 * otherwise read no economy state at all is how one of them comes to answer
 * differently from the other four.
 *
 * FALSE IS THE SAFE ANSWER AND IT IS THE DEFAULT. The slice starts
 * `enabled: false`, so every one of those surfaces renders today's copy until a
 * successful read says otherwise, which is exactly the flag-off promise.
 *
 * @param {object} state the root state
 * @returns {boolean}
 */
export const selectEconomyVersioned = (state) =>
  state?.economy?.enabled === true &&
  state?.economy?.incentiveVersion !== null &&
  state?.economy?.incentiveVersion !== undefined;

/**
 * A LEVEL'S READER MODE, FROM THE READ (B2.1 item 3, W4-F2.1).
 *
 * `levels[].reader_mode` is `scored`, `completion_only`, or null. Null is
 * UNAVAILABLE and is never repaired into either mode: the server serves null
 * when the cohort's `incentive_version` has no `level_thresholds` row for that
 * capsule, and on such a level the upload path writes no floor status and the
 * floor transition never runs, so a client that guessed `scored` there would
 * wait for a result that is not coming.
 *
 * ANY OTHER STRING IS ALSO UNAVAILABLE. The server validates the column against
 * the two modes before serializing it, so a third value can only mean the
 * contract has moved, and a moved contract is not a mode to act on.
 *
 * @param {Array|undefined} levels the read's `levels[]`
 * @param {number|string|null} capsuleId
 * @returns {"scored"|"completion_only"|null}
 */
export const readerModeOf = (levels, capsuleId) => {
  if (!Array.isArray(levels)) return null;
  if (capsuleId === null || capsuleId === undefined) return null;
  const row = levels.find(
    (level) => Number(level?.capsule_id) === Number(capsuleId),
  );
  const mode = row?.reader_mode ?? null;
  return mode === "scored" || mode === "completion_only" ? mode : null;
};

/**
 * WHICH OUTPUT FACT SURVIVES WHEN A READ AND A SOCKET DISAGREE.
 *
 * The read is authoritative for everything EXCEPT a transition that landed
 * after the read left the server. That case is real and is not rare: the drawer
 * fires a refresh at the upload, the floor result lands on the socket a second
 * later, and the read that was already in flight answers with `pending` for the
 * same attempt. Taking the read there would move the learner backwards from a
 * result they have already been given, and the ending would never play.
 *
 * So the socket's fact is kept when it is about a LATER attempt, or about the
 * SAME attempt and the read is still `pending` while the socket is terminal.
 * Every other case is the read's, including a socket fact about an attempt
 * below the read's, which is the stale-result rule of the floor state table
 * (technical brief 5.3, row 12).
 *
 * AND IT IS SCOPED BY CAPSULE, WHICH IS THE WHOLE OF THE THIRD ARGUMENT.
 * `attempt` RESTARTS AT 1 ON EVERY CAPSULE: `milestone_files` is unique on
 * `(capsule_id, squad_id)` and the submission controller seeds a new row's
 * attempt at 1, so it is not a counter that rises across a run. Without the
 * capsule check a `floor` about last week's level, a teacher override on a
 * level the squad already shipped, or a squadmate's late resubmission reads as
 * "a newer attempt" than the current level's empty output and releases an
 * ending for a level whose own output has not been read yet. The floor payload
 * carries `capsule_id` (`FloorTransition.php`), so the information to refuse it
 * is on the wire.
 *
 * @param {object} readOutput the `output` block the self read served
 * @param {object|null} socketFloor the newest accepted floor transition
 * @param {number|null} outputCapsuleId the capsule `readOutput` belongs to. A
 *   null means the client cannot say, and a fact that cannot be scoped is not
 *   merged.
 * @returns {object} the output the slice should hold
 */
export const mergeOutput = (
  readOutput,
  socketFloor,
  outputCapsuleId = null,
) => {
  const base = readOutput ?? EMPTY_OUTPUT;
  if (!socketFloor || socketFloor.capsuleId === null) return base;
  if (
    outputCapsuleId === null ||
    outputCapsuleId === undefined ||
    Number(socketFloor.capsuleId) !== Number(outputCapsuleId)
  ) {
    return base;
  }
  const readAttempt = base.attempt === null ? -1 : Number(base.attempt);
  const socketAttempt =
    socketFloor.attempt === null ? -1 : Number(socketFloor.attempt);
  const newerAttempt = socketAttempt > readAttempt;
  const sameAttemptFresherResult =
    socketAttempt === readAttempt &&
    !FLOOR_TERMINAL.includes(base.floor_status) &&
    FLOOR_TERMINAL.includes(socketFloor.status);
  if (!newerAttempt && !sameAttemptFresherResult) return base;
  return {
    ...base,
    attempt: socketFloor.attempt,
    floor_status: socketFloor.status,
    floor_hint: socketFloor.hint ?? null,
    /* THE REASON ALWAYS COMES FROM THE SAME SOURCE AS THE STATUS THAT WON. A
       newer socket fact with no reason clears a stale reason held from an
       older read; when the read wins (the early returns above), the read's own
       reason travels with the read's own status. */
    reader_failure_reason: socketFloor.readerFailureReason ?? null,
  };
};

/**
 * Should this socket message be acted on at all?
 *
 * FIVE REFUSALS, and each one has a failure it prevents.
 *
 *   NOT A MESSAGE THIS SLICE CONSUMES. The socket carries points, activity,
 *   plan and presence traffic as well; a refresh per keystroke of a squadmate's
 *   plan edit would be a read storm.
 *
 *   NO ANSWER HELD YET, AND THAT IS A QUESTION ABOUT THE HELD PAYLOAD RATHER
 *   THAN ABOUT `status`. The first version of this asked `status !==
 *   "succeeded"`, and `status` is `loading` for the whole of every REFRESH:
 *   the upload tail asks for one, and every accepted message asks for one. A
 *   `floor passed` that landed inside that window was dropped whole, without
 *   its event id recorded and without a refresh token, and the read that was
 *   already in flight had left the server BEFORE the transition it reports.
 *   That is precisely the case `mergeOutput` exists for. So the gate is a held
 *   payload: `levels` is empty until a success lands and `clearPayload` empties
 *   it again on a scope change, a 401 and a 403, which is the same door with
 *   none of the refresh window.
 *
 *   ANOTHER COHORT, ANOTHER SQUAD, OR ANOTHER CHALLENGE'S CAPSULE. A learner in
 *   two cohorts shares one socket, and `floor` and `send_back` carry a
 *   `squad_id` that a shared channel can make foreign. `capsule_id` is nullable
 *   on an economy event, so a null capsule is scoped by cohort alone and a
 *   present one must be a capsule this read holds.
 *
 *   AN EVENT ID ALREADY SEEN. Replays and reconnect backfills are ordinary, and
 *   a duplicate must produce no second refresh and no second ending.
 */
export const acceptsSocketMessage = (state, message) => {
  if (!message || !ECONOMY_MESSAGE_TYPES.includes(message.type)) return false;
  if (state.enabled !== true) return false;
  if (!Array.isArray(state.levels) || state.levels.length === 0) return false;
  if (!message.event_id) return false;
  if (state.seenEventIds.includes(message.event_id)) return false;
  if (
    message.cohort_id !== undefined &&
    message.cohort_id !== null &&
    state.cohortId !== null &&
    Number(message.cohort_id) !== Number(state.cohortId)
  ) {
    return false;
  }
  if (
    !SQUAD_SCOPE_EXEMPT.includes(message.type) &&
    message.squad_id !== undefined &&
    message.squad_id !== null &&
    state.squadId !== null &&
    Number(message.squad_id) !== Number(state.squadId)
  ) {
    return false;
  }
  if (message.capsule_id !== undefined && message.capsule_id !== null) {
    const held = state.levels.some(
      (level) => Number(level.capsule_id) === Number(message.capsule_id),
    );
    if (!held) return false;
  }
  return true;
};

/**
 * Should the ONE Dashboard effect dispatch the economy read right now?
 *
 * Pure, so the decision is table tested rather than reachable only through the
 * component, exactly as `shouldFetchCompletion` is. `force` is the socket's
 * refresh token, the reconnect, and the completion boundary: all three are
 * events after which the held answer is known to be out of date, and none of
 * them is a scope change, so without it a re-read would be indistinguishable
 * from a re-render.
 */
export const shouldFetchEconomy = ({
  userId,
  challengeId,
  status,
  scopeKey,
  force = false,
}) => {
  if (!userId || !challengeId) return false;
  if (status === "loading") return false;
  const nextScopeKey = economyScopeKey({ userId, challengeId });
  if (status === "idle" || scopeKey !== nextScopeKey) return true;
  return force === true;
};

/**
 * EVERYTHING THE SQUAD OWNS, AND NOTHING ELSE (W4-F2.6 delta fix batch item 1,
 * Codex delta review NEW MAJOR).
 *
 * `economyScopeKey` is `${userId}:${challengeId}` and carries NO squad, and
 * widening it is not available: the key is compared against
 * `economyScopeKey(action.meta.arg)` in all three `fetchEconomy` reducers, and
 * no call site puts a squad in that arg, so a widened key would never match
 * its own responses and every read would be dropped on the floor. So a squad
 * reassignment INSIDE one challenge, which is a shipped teacher capability
 * (`Manage/SquadTopologyController`, and the move flow named in
 * `TeacherOnboardingController`), changes `squadId` here and changes nothing
 * the key can see: the next `fetchEconomy.pending` finds the same key and does
 * not `clearPayload`.
 *
 * WHAT IT LEAVES BEHIND IS THE OLD SQUAD'S ANSWER TO A SQUAD QUESTION. Audited
 * against `EconomyReadDTO` (deployed backend `f9cb1de7`), these are the payload
 * keys the server derives from the squad:
 *
 *   `output`    `milestone_files where squad_id = ? and capsule_id = ?`, and
 *               its `capsule_id` is the squad's current level. This is the one
 *               Codex reproduced: `selectSquadCurrentCapsuleId` prefers it over
 *               the projection, the projection re-scopes correctly (its own key
 *               carries the squad), so the page reads the reassigned learner
 *               behind their real squad on every level indicator at once.
 *   `squad`     the standing, the neighbours, the gate counts and the level
 *               awards, all `$aggregate->standing($squadId)`.
 *   `levels`    `squad_status`, `standing` and `level_standing`, per capsule,
 *               all of them the squad's.
 *   `personal`  the learner's own XP, but keyed on `$capsuleId`, which the DTO
 *               resolves as the SQUAD's current level. A learner's numbers for
 *               the wrong level are the wrong numbers.
 *   `vault`     `VaultReadDTO::read(..., $squadId, ...)`.
 *
 * AND THESE ARE NOT THE SQUAD'S, so they are kept: `enabled`,
 * `incentiveVersion`, `rules`, `weights`, `finaleWeight`, `runClosed` and
 * `vaultPrize` are cohort or run facts; `kickoffSeen` is
 * `economy_user_states` by cohort and user; `notices` is queried by
 * `notices.user_id` and the cohort, and each row's `mine` was decided when the
 * award was announced, which a later move does not retroactively change.
 * `enabled` in particular MUST survive, because every Wave 4 surface is
 * mounted on it and clearing it would unmount a playing ending, which is the
 * same harm the `rejected` reducer below refuses for a transient 5xx.
 */
const clearSquadOwned = (state) => {
  state.personal = null;
  state.squad = null;
  state.output = EMPTY_OUTPUT;
  state.levels = [];
  state.vault = null;
};

/** Everything a scope change or a refusal drops. */
const clearPayload = (state) => {
  state.enabled = false;
  state.incentiveVersion = null;
  state.personal = null;
  state.squad = null;
  state.output = EMPTY_OUTPUT;
  state.vault = null;
  state.vaultPrize = null;
  state.weights = [];
  state.finaleWeight = null;
  state.runClosed = false;
  state.kickoffSeen = false;
  state.levels = [];
  state.notices = [];
  state.rules = null;
  state.floor = null;
  state.seenEventIds = [];
};

const economySlice = createSlice({
  name: "economy",
  initialState,
  reducers: {
    /** Explicit teardown when the surface family is left entirely. */
    clearEconomy: () => initialState,

    /**
     * The cohort the socket is allowed to speak for. Dispatched by the one
     * component that knows it, and idempotent so a re-render costs nothing.
     */
    scopeEconomy: (state, action) => {
      const nextCohort = action.payload?.cohortId ?? null;
      const nextSquad = action.payload?.squadId ?? null;
      /**
       * TWO MISSING IDS ARE THE SAME SCOPE (CodeRabbit, PR 432).
       *
       * `Number(null ?? NaN) === Number(null ?? NaN)` is FALSE, so the first
       * dispatch of this action -- which the Dashboard makes before the cohort
       * and squad are known -- read as a scope CHANGE and cleared
       * `seenEventIds` and `floor`. `acceptsSocketMessage` skips the scope
       * check while a stored id is null, so a message accepted in that window
       * had already written both, and the null-scope dispatch threw them away:
       * a floor transition lost, and its event id free to be applied twice.
       * Present ids keep numeric equality, so "3" and 3 are still one scope.
       */
      const sameId = (held, next) =>
        held === null ||
        held === undefined ||
        next === null ||
        next === undefined
          ? (held ?? null) === (next ?? null)
          : Number(held) === Number(next);
      const sameCohort = sameId(state.cohortId, nextCohort);
      const sameSquad = sameId(state.squadId, nextSquad);
      /**
       * ONE PRESENT SQUAD REPLACED BY ANOTHER IS THE ONLY REPLACEMENT (W4-F2.6
       * delta fix batch item 1). Read BEFORE the assignment below, because
       * after it the held id is the new one.
       *
       * A SQUAD GOING UNKNOWN IS NOT A DIFFERENT SQUAD, and the difference is
       * the whole of this condition. The Dashboard's scope effect dispatches
       * `{ cohortId: null, squadId: null }` on the first pass of any mount
       * whose user object has not rehydrated yet; treating that as a
       * replacement would blank the header, the standing and the output every
       * time the page remounted, which is a worse failure than the one this
       * fixes and is exactly the harm `clearSquadOwned` is scoped to avoid.
       * The clear then happens at the second dispatch, when the new squad is
       * actually known, which is the same moment the read it invalidates could
       * first be replaced.
       */
      const squadReplaced =
        state.squadId !== null &&
        state.squadId !== undefined &&
        nextSquad !== null &&
        nextSquad !== undefined &&
        Number(state.squadId) !== Number(nextSquad);
      if (sameCohort && sameSquad) return;
      state.cohortId = nextCohort;
      state.squadId = nextSquad;
      // A different cohort's or squad's event ids are not this scope's history.
      state.seenEventIds = [];
      state.floor = null;
      if (!squadReplaced) return;
      clearSquadOwned(state);
      /**
       * AND THE READ IS ASKED FOR AGAIN RATHER THAN WAITED FOR. `scopeKey` has
       * not moved, so `shouldFetchEconomy` would answer false for a
       * `succeeded` read and the cleared numbers would stay blank until some
       * unrelated staleness signal arrived. `idle` is the one arm of that
       * function that means "there is no answer here", which is now true.
       *
       * AND A READ ALREADY IN FLIGHT IS INVALIDATED, `requestId` AND ALL
       * (CodeRabbit PR 439, MAJOR). The first version of this line left a
       * `loading` read alone, on the reasoning that `loading` is the guard
       * keeping this slice to one outstanding request. It is, and that was the
       * defect: a read dispatched BEFORE the move answers with the OLD squad's
       * `output`, `squad`, `levels`, `personal` and `vault`, its scope key is
       * `${userId}:${challengeId}` and therefore unmoved, and its request id
       * was still the one held here. Both guards in the `fulfilled` reducer
       * passed and the clear above was undone one tick after it happened.
       *
       * NULLING THE ID IS WHAT MAKES THAT RESPONSE UNOWNABLE. Every dispatch
       * carries a non-empty `requestId` string, so `fulfilled` and `rejected`
       * both return at their request-id line and the payload is dropped
       * wherever it lands in the sequence. The server's own answer is not the
       * point: the route resolves the squad at handling time, so the response
       * may even be the new squad's, and the client cannot tell which. It
       * refuses the one it cannot attribute and asks again.
       *
       * THE COST IS ONE ORPHANED REQUEST, WHICH IS THE RIGHT TRADE. The read
       * owner starts a replacement on the next commit, the orphan settles into
       * nothing, and the slice is back to one outstanding read. What the old
       * line bought instead was a window in which the page painted the squad
       * the learner had just been moved out of.
       */
      state.requestId = null;
      state.status = "idle";
    },

    /**
     * A SOCKET MESSAGE, WHICH IS A STALENESS NOTICE AND ALMOST NEVER A VALUE.
     *
     * It records the event id, records a floor transition when the message
     * carries one, and bumps the refresh token. It writes no personal number,
     * no squad number and no Vault balance, ever: those come from the
     * authenticated read and from nowhere else (technical brief 4.4).
     */
    economySocketMessage: (state, action) => {
      const message = action.payload ?? null;
      if (!acceptsSocketMessage(state, message)) return;
      state.seenEventIds = [...state.seenEventIds, message.event_id].slice(
        -SEEN_EVENT_CAP,
      );
      if (message.type === "floor") {
        const floor = {
          eventId: message.event_id,
          capsuleId:
            message.capsule_id === undefined || message.capsule_id === null
              ? null
              : Number(message.capsule_id),
          attempt:
            message.attempt === undefined || message.attempt === null
              ? null
              : Number(message.attempt),
          status: message.status ?? null,
          hint: message.hint ?? null,
          readerFailureReason: message.reader_failure_reason ?? null,
        };
        /* A RESULT FOR AN OLDER ATTEMPT IS NOT A RESULT (row 12). It is still
           deduplicated above, so a replay of it costs one refresh and no more,
           but it never replaces the fact the client holds. */
        const heldAttempt =
          state.floor?.attempt === null || state.floor?.attempt === undefined
            ? -1
            : Number(state.floor.attempt);
        const sameCapsule =
          state.floor !== null &&
          Number(state.floor.capsuleId) === Number(floor.capsuleId);
        const older =
          sameCapsule &&
          floor.attempt !== null &&
          Number(floor.attempt) < heldAttempt;
        if (!older) {
          /* THE FACT IS RECORDED WHATEVER CAPSULE IT IS ABOUT, because a
             teacher override on last week's level is a real transition and the
             dashboard marks the projection stale on it. What is scoped is the
             MERGE: only a fact about the capsule the read's `output` belongs to
             may change that output. */
          state.floor = floor;
          state.output = mergeOutput(
            state.output,
            floor,
            outputCapsuleIdOf(state.output),
          );
        }
      }
      /**
       * AN `award` MESSAGE IS A STALENESS NOTICE AND NOTHING ELSE (W4-F2.1
       * item 6).
       *
       * F2 recorded its `level_award_id -> capsule_id` pair here, because the
       * read's `notices[]` carried the level number and `squad.level_awards[]`
       * is keyed by capsule, so the message was the only place the pop-up's
       * join could be made exactly. B2.1 serves `notices[].capsule_id`, so the
       * join is in the read on BOTH delivery paths and a socket-only map would
       * be a second, weaker answer to a question the read now answers. The
       * refresh this message triggers is what fetches the notice, as before.
       */
      if (message.type === "send_back") {
        /* The comment itself is the read's; what is held here is that the
           generation moved, which is what invalidates a parked ship event.
           SCOPED BY CAPSULE for the same reason the floor merge is: a send back
           on a level the squad already closed is not this level's review
           state. */
        const sendBackCapsule =
          message.capsule_id === undefined || message.capsule_id === null
            ? null
            : Number(message.capsule_id);
        const outputCapsule = outputCapsuleIdOf(state.output);
        if (
          sendBackCapsule !== null &&
          outputCapsule !== null &&
          sendBackCapsule === outputCapsule
        ) {
          state.output = {
            ...state.output,
            review_state: "sent_back",
          };
        }
      }
      state.refreshToken += 1;
    },

    /**
     * The socket reconnected. Whatever was missed while it was down is missed,
     * so the read is the only way back to the truth.
     */
    economyReconnected: (state) => {
      if (state.status !== "succeeded") return;
      state.refreshToken += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEconomy.pending, (state, action) => {
        const nextScopeKey = economyScopeKey(action.meta.arg);
        if (nextScopeKey !== state.scopeKey) {
          // Scope switched. Drop the prior learner's or the prior challenge's
          // answer NOW rather than when the new one lands: the gap between the
          // two is where somebody else's standing would render.
          clearPayload(state);
          state.error = null;
          state.errorStatus = null;
          state.scopeKey = nextScopeKey;
        }
        state.requestId = action.meta.requestId;
        state.status = "loading";
        state.generation += 1;
      })
      .addCase(fetchEconomy.fulfilled, (state, action) => {
        if (economyScopeKey(action.meta.arg) !== state.scopeKey) return;
        if (action.meta.requestId !== state.requestId) return;
        const data = action.payload ?? {};
        state.status = "succeeded";
        state.error = null;
        state.errorStatus = null;
        /* EVERY FIELD IS READ THROUGH A DEFAULT, and the defaults are the
           WITHHELD answers rather than convenient ones: a payload missing
           `enabled` renders nothing, a payload missing `squad` offers no
           standing, and a payload missing `vault` plays no Vault beat. A server
           that stopped carrying a field is a contract break, and the fixture
           binding test is where that is caught, not a screen that invented a
           value for it. */
        state.enabled = data.enabled === true;
        state.incentiveVersion = data.incentive_version ?? null;
        state.personal = data.personal ?? null;
        state.squad = data.squad ?? null;
        state.levels = Array.isArray(data.levels) ? data.levels : [];
        /* THE CAPSULE COMES FROM THE INCOMING READ'S OWN `output` BLOCK and
           not from the held state, which at this line is still the previous
           read's: a learner whose squad has moved to the next level would
           otherwise have this read's output scoped by last level's capsule. */
        state.output = mergeOutput(
          data.output ?? EMPTY_OUTPUT,
          state.floor,
          outputCapsuleIdOf(data.output),
        );
        state.vault = data.vault ?? null;
        /* ABSENT READS AS NO PRIZE. A server that does not serve the key has
           not said there is one, and the kickoff's line is then dropped exactly
           as it is today.

           AND WHAT IS STORED IS THE STATE, NEVER THE BLOCK (F2.3 fix batch
           item 2, Codex B2). Every render path reads presence only, so nothing
           on screen leaks anything today; what this line decides is whether a
           SERVER regression could put an amount into client state at all. The
           amount is a NO BUILD trigger by name (technical brief v18 section 9
           item 5, design brief v15 section 7, Amir 2026-09-11: they do not
           learn how much is in the pot), the B2.6 backend seat had already
           blocked once on a payload carrying it, and Redux state is reachable
           from DevTools, from any state-aware error reporter and from the next
           careless read. So the reducer is the boundary: the block is opened
           here, one named field is taken out of it, and the object itself is
           dropped on the floor. */
        /* Kickoff refinement BE0/C: THE CURRENCY IS THE ONE OTHER FIELD KEPT,
           and only a code in the pinned display list (`isDisplayCurrency`, a
           byte copy of the backend's allowlist). Anything else is `null`, which
           the Vault demo draws as the numberless fill. The amount, the sponsor
           and the legal reference remain forbidden and are never read. */
        state.vaultPrize = data.vault_prize
          ? {
              state: data.vault_prize.state ?? null,
              unit: isDisplayCurrency(data.vault_prize.unit)
                ? data.vault_prize.unit
                : null,
            }
          : null;
        state.weights = Array.isArray(data.weights) ? data.weights : [];
        state.finaleWeight = data.finale_weight ?? null;
        state.runClosed = data.run_closed === true;
        state.kickoffSeen = data.kickoff_seen === true;
        state.notices = Array.isArray(data.notices) ? data.notices : [];
        state.rules = data.rules ?? null;
      })
      .addCase(fetchEconomy.rejected, (state, action) => {
        if (action.meta.aborted) return;
        if (
          action.error?.name === "CanceledError" ||
          action.error?.name === "AbortError"
        ) {
          return;
        }
        if (economyScopeKey(action.meta.arg) !== state.scopeKey) return;
        if (action.meta.requestId !== state.requestId) return;
        state.status = "failed";
        state.error = action.payload?.message ?? "Failed to load economy.";
        state.errorStatus = action.payload?.status ?? null;
        /**
         * A REFUSAL CLEARS. A FAILURE DOES NOT.
         *
         * The uniform 403 is what a caller without a single active squad in the
         * challenge's cohort gets, which includes staff, an exited member and
         * an impersonation view; 401 is the same answer from a session that has
         * ended. Both are the server saying "not you", and holding a standing
         * through one would paint a squad's numbers for somebody it has just
         * declined to answer.
         *
         * EVERY OTHER FAILURE KEEPS THE HELD PAYLOAD, and the difference is not
         * cosmetic. `enabled` is what every Wave 4 surface is mounted on, so a
         * transient 5xx that cleared it would unmount a playing ending and
         * strip the header mid-sequence. `status: "failed"` still says so.
         */
        if (state.errorStatus === 403 || state.errorStatus === 401) {
          clearPayload(state);
        }
      });
  },
});

export const {
  clearEconomy,
  economyReconnected,
  economySocketMessage,
  scopeEconomy,
} = economySlice.actions;

export default economySlice.reducer;
